import { ChangeDetectorRef, Component, ChangeDetectionStrategy } from '@angular/core';
import { EMAIL_REGEX } from '@core/enums/patterns';
import { TableActions } from '@core/enums/table-actions';
import { IAction } from '@core/interfaces/i-action';
import { IFormInput } from '@core/interfaces/iform-input';
import { IModalData } from '@core/interfaces/imodal-data';
import { AuthService } from '@core/services/auth.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { ConfirmCancelComponent } from 'src/app/component/shared/confirm-cancel/confirm-cancel.component';
import { ModalFormComponent } from 'src/app/component/shared/modal-form/modal-form.component';
import { MailingListService } from '@core/services/mailing-list.service';
import { ISubscriber } from '@core/interfaces/isubscriber';
import { faFileImport, faDownload, faPlus } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-subscribers',
    templateUrl: './subscribers.component.html',
    styleUrls: ['./subscribers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class SubscribersComponent {
  faFileImport = faFileImport;
  faDownload = faDownload;
  faPlus = faPlus;

  subscribers!: any[];
  totalSubscribers: number = 0;

  tableActions = TableActions;

  constructor(
    private modal: NgbModal,
    private mailingList: MailingListService,
    private toast: ToastrService,
    private auth: AuthService,
  ) {
    this.getTotalSubscribers();
  }

  inputs: IFormInput[] = [
    {
      controlName: 'email',
      label: 'Email',
      type: 'email',
      maxLength: 100,
      helper: '(Max length: 100 characters)',
      validators: [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(EMAIL_REGEX),
      ],
      required: true,
    },
  ];

  addModal: IModalData = {
    title: 'Add New Subscriber',
    cancelBtnText: 'Cancel',
    confirmBtnText: 'Add Subscriber',
    formInputs: this.inputs,
  };

  editModal: IModalData = {
    title: 'Edit Subscriber',
    cancelBtnText: 'Cancel & Discard',
    confirmBtnText: 'Save Subscriber',
    formInputs: this.inputs,
  };

  deleteModal: IModalData = {
    title: 'Delete Subscriber',
    body: 'Are you sure you want to delete this Subscriber?',
    cancelBtnText: 'Cancel',
    confirmBtnText: 'Delete',
  };

  getTotalSubscribers(): void {
    this.mailingList
      .getAll()
      .pipe(take(1))
      .subscribe((subscribers: ISubscriber[]): void => {
        this.totalSubscribers = subscribers.length;
      });
  }

  handleSubscribers(event: IAction): void {
    switch (event.col.label) {
      case TableActions.Add:
        this.openModal(this.addModal, ModalFormComponent)
          .result.then((res: any) => {
            this.auth
              .isDuplicateEmail(res.email)
              .pipe(take(1))
              .subscribe((success: boolean) => {
                if (success) {
                  this.toast.error('Email Already Exists. Please try again with different email.');
                } else {
                  this.mailingList
                    .addSubscriber(res, 'Email successfully added to mailing list.')
                    .pipe(take(1))
                    .subscribe((res: any) => {
                      window.location.reload();
                    });
                }
              });
          })
          .catch((_err: any) => false);
        break;
      case TableActions.Edit:
        this.editModal.data = event.record;
        this.openModal(this.editModal, ModalFormComponent)
          .result.then((res: any) => {
            res.id = event.record.id;
            this.mailingList
              .update(res, 'Email Updated.')
              .pipe(take(1))
              .subscribe((res: any) => {
                window.location.reload();
              });
          })
          .catch((_err: any) => false);
        break;
      case TableActions.Delete:
        this.deleteModal.body = `
          <p>
          You are about to delete:</br>
          <b>${event.record.email} </b>.</br></br>
          Are you sure? <i>This action cannot be undone.</i></p>`;
        this.openModal(this.deleteModal, ConfirmCancelComponent)
          .result.then((_res) => {
            this.mailingList
              .deleteSubscriber(event.record.id, 'Email deleted.')
              .pipe(take(1))
              .subscribe((_res: any) => {
                window.location.reload();
              });
          })
          .catch((_err: any) => false);
        break;
      default:
        this.toast.error('Uh oh! No action found.');
    }
  }

  openModal(modalData: IModalData, modalType: any): NgbModalRef {
    const modalRef = this.modal.open(modalType, { centered: true });
    modalRef.componentInstance.data = modalData;
    return modalRef;
  }

  copyAllEmails(): void {
    this.mailingList.getAll().subscribe((mailingList: { email: string }[]) => {
      const allEmails = mailingList
        .map((s: { email: string }) => s.email)
        .join('; ');
      const tempTextarea = document.createElement('textarea');
      tempTextarea.value = allEmails;
      document.body.appendChild(tempTextarea);
      tempTextarea.select();
      tempTextarea.setSelectionRange(0, tempTextarea.value.length);
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          this.toast.success('Emails copied to clipboard');
        } else {
          this.promptCopyFallback(allEmails);
        }
      } catch (error) {
        this.promptCopyFallback(allEmails);
      }
      document.body.removeChild(tempTextarea);
    });
  }

  private promptCopyFallback(text: string): void {
    const copyPrompt = prompt('Press Ctrl+C (or Command+C on Mac) to copy the emails:', text);
    if (copyPrompt) {
      this.toast.success('Emails copied to clipboard');
    } else {
      this.subscribers = [];
    }
  }

  exportToCsv(): void {
    this.mailingList.getAll().subscribe((mailingList: { email: string }[]) => {
      const emails: string[] = mailingList.map((subscriber: { email: any }) => subscriber.email);
      const csvData: string = 'data:text/csv;charset=utf-8,' + emails.join('\n');
      const encodedUri: string = encodeURI(csvData);
      const link: HTMLAnchorElement = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'emails.csv');
      document.body.appendChild(link);
      link.click();
    });
  }
}
